import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { colors, fonts, shadows } from "../theme";

// Guided onboarding walkthrough. Spotlights each key area in sequence:
// import → left tab (Building/Neighborhood) → Show Possibilities → Tools → right rail
// (Site & Climate → Building Configuration → Circularity Rings) → the Dashboard.
// Steps that only exist once a building is loaded are reached after the import step
// auto-loads the sample building (per product spec).
const STEPS = [
  {
    id: "import",
    selectors: ['[data-testid="viewport-empty-state"]', '[data-testid="model-filebar"]'],
    title: "Import your building",
    body: "Begin here. Drag in a Rhino or CAD model (.3dm, .obj, or .glb), or open a saved .rcengine project. If you do not have a model, we can load a sample so you can explore.",
    place: "auto",
  },
  {
    id: "scope",
    selectors: ['[data-testid="scope-toggle"]'],
    title: "Building vs Neighborhood",
    body: "\u201CBuilding\u201D includes only your primary building. Switch to \u201CNeighborhood\u201D to include the surrounding context masses in every calculation.",
    place: "right",
  },
  {
    id: "possibilities",
    selectors: ['[data-testid="analysis-toggle"]'],
    title: "Show Possibilities",
    body: "Highlights the viable growing surfaces for the active loop using a solar and structural heat-map, showing where food can be grown.",
    place: "right",
  },
  {
    id: "tools",
    selectors: ['[data-testid="tool-dock"]'],
    title: "Tools",
    body: "Measure distances, cut a section box, save the scenario, export the project, or generate a PDF report — all from one dock.",
    place: "right",
  },
  {
    id: "site",
    selectors: ['[data-testid="group-site-climate"]'],
    title: "Site & Climate",
    body: "Pin your location to automatically retrieve daylight, solar, wind, and rainfall data. All downstream metrics are calibrated to this real site.",
    place: "left",
    expand: "group-site-climate",
  },
  {
    id: "config",
    selectors: ['[data-testid="group-building-config"]'],
    title: "Building Configuration",
    body: "Set the typology, occupancy, and roof reserve. These determine your realistic, growable opportunity area.",
    place: "left",
    expand: "group-building-config",
  },
  {
    id: "rings",
    selectors: ['[data-testid="group-rings"]'],
    title: "Circularity Loops",
    body: "The heart of the studio — allocate Zones \u2192 Systems \u2192 Crops \u2192 Resources and watch the circular loops close.",
    place: "left",
  },
  {
    id: "dashboard",
    selectors: ['[data-testid="kpi-ribbon"]'],
    title: "Live Dashboard",
    body: "Track yield, water, energy, carbon, and your Circularity Score in real time. Expand it for charts, resource balances, and saved scenarios.",
    place: "top",
  },
];

export default function QuickTour({ open, onClose, onLoadSample, modelStatus }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [waiting, setWaiting] = useState(false);
  const waitingRef = useRef(false);

  const step = STEPS[idx];

  const resolveTarget = useCallback(() => {
    if (!step) return null;
    for (const sel of step.selectors) {
      const el = document.querySelector(sel);
      if (el) return el;
    }
    return null;
  }, [step]);

  // reset to the first step every time the tour (re)opens
  useEffect(() => {
    if (open) { setIdx(0); setWaiting(false); waitingRef.current = false; }
  }, [open]);

  // On step change: expand the target group if collapsed, then scroll it into view.
  useEffect(() => {
    if (!open || !step) return;
    if (step.expand) {
      const grp = document.querySelector(`[data-testid="${step.expand}"]`);
      if (grp && grp.getAttribute("data-open") !== "true") {
        document.querySelector(`[data-testid="${step.expand}-toggle"]`)?.click();
      }
    }
    const t = resolveTarget();
    if (t) t.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [open, idx, step, resolveTarget]);

  // Keep the spotlight glued to the target through scroll / resize / layout settle.
  useEffect(() => {
    if (!open) return;
    const measure = () => {
      const t = resolveTarget();
      if (!t) { setRect(null); return; }
      const r = t.getBoundingClientRect();
      setRect((prev) =>
        prev && Math.abs(prev.left - r.left) < 1 && Math.abs(prev.top - r.top) < 1 &&
        Math.abs(prev.width - r.width) < 1 && Math.abs(prev.height - r.height) < 1
          ? prev
          : { left: r.left, top: r.top, width: r.width, height: r.height }
      );
    };
    measure();
    const iv = setInterval(measure, 150);
    window.addEventListener("resize", measure);
    return () => { clearInterval(iv); window.removeEventListener("resize", measure); };
  }, [open, idx, resolveTarget]);

  // After requesting the sample load on the import step, advance once the model is ready.
  useEffect(() => {
    if (waiting && modelStatus === "ready") {
      setWaiting(false); waitingRef.current = false;
      setIdx((i) => Math.min(i + 1, STEPS.length - 1));
    }
  }, [waiting, modelStatus]);

  // Esc closes the tour
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const finish = () => onClose?.();
  const back = () => setIdx((i) => Math.max(0, i - 1));
  const next = () => {
    if (idx >= STEPS.length - 1) { finish(); return; }
    if (step.id === "import" && modelStatus !== "ready") {
      setWaiting(true); waitingRef.current = true; onLoadSample?.(); return;
    }
    setIdx((i) => Math.min(i + 1, STEPS.length - 1));
  };

  // ---- bubble placement ----
  const BW = 328;
  const vw = window.innerWidth, vh = window.innerHeight;
  const bubbleStyle = { maxWidth: BW };
  if (rect) {
    const place = step.place || "auto";
    if (place === "left") {
      bubbleStyle.right = vw - rect.left + 16;
      bubbleStyle.top = Math.max(16, Math.min(rect.top, vh - 250));
    } else if (place === "right") {
      bubbleStyle.left = rect.left + rect.width + 16;
      bubbleStyle.top = Math.max(16, Math.min(rect.top, vh - 250));
    } else if (place === "top") {
      bubbleStyle.left = Math.max(16, Math.min(rect.left, vw - BW - 16));
      bubbleStyle.bottom = vh - rect.top + 16;
    } else {
      bubbleStyle.left = Math.max(16, Math.min(rect.left + rect.width / 2 - BW / 2, vw - BW - 16));
      bubbleStyle.top = Math.min(rect.top + rect.height + 16, vh - 250);
    }
  } else {
    bubbleStyle.left = vw / 2 - BW / 2;
    bubbleStyle.top = vh / 2 - 110;
  }

  const pad = 8;

  return createPortal(
    <div style={styles.root} data-testid="quick-tour">
      <div style={styles.block} />
      {rect && (
        <div
          style={{ ...styles.spot, left: rect.left - pad, top: rect.top - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }}
          data-testid="quick-tour-spotlight"
        />
      )}
      <div style={{ ...styles.bubble, ...bubbleStyle }} data-testid="quick-tour-bubble">
        <div style={styles.counter} data-testid="quick-tour-counter">{idx + 1} / {STEPS.length}</div>
        <div style={styles.title}>{step.title}</div>
        <div style={styles.body}>{waiting ? "Loading the sample building\u2026" : step.body}</div>
        <div style={styles.actions}>
          <button style={styles.skip} onClick={finish} data-testid="quick-tour-skip">Skip Tour</button>
          <div style={{ display: "flex", gap: 8 }}>
            {idx > 0 && (
              <button style={styles.back} onClick={back} data-testid="quick-tour-back" disabled={waiting}>Back</button>
            )}
            <button style={{ ...styles.next, ...(waiting ? styles.nextDisabled : {}) }} onClick={next} data-testid="quick-tour-next" disabled={waiting}>
              {idx >= STEPS.length - 1 ? "Finish" : "Next"}
            </button>
          </div>
        </div>
        <div style={styles.dots}>
          {STEPS.map((s, i) => (
            <span key={s.id} style={{ ...styles.dot, ...(i === idx ? styles.dotOn : {}) }} />
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

const styles = {
  root: { position: "fixed", inset: 0, zIndex: 200 },
  block: { position: "absolute", inset: 0, background: "transparent", cursor: "default" },
  spot: {
    position: "fixed", borderRadius: 14, border: "2px solid #fff", pointerEvents: "none",
    boxShadow: "0 0 0 4px rgba(81,170,254,0.55), 0 0 0 9999px rgba(12,14,18,0.62)",
    transition: "left 0.28s cubic-bezier(0.16,1,0.3,1), top 0.28s cubic-bezier(0.16,1,0.3,1), width 0.28s cubic-bezier(0.16,1,0.3,1), height 0.28s cubic-bezier(0.16,1,0.3,1)",
  },
  bubble: {
    position: "fixed", width: 328, background: colors.surface, borderRadius: 18,
    border: `1px solid ${colors.border}`, boxShadow: shadows.float, padding: "18px 20px 16px", zIndex: 202,
  },
  counter: { fontFamily: fonts.mono, fontSize: 10, letterSpacing: 1.4, color: colors.textTertiary, fontWeight: 700 },
  title: { fontFamily: fonts.sans, fontSize: 17, fontWeight: 700, letterSpacing: -0.3, color: colors.textPrimary, marginTop: 6 },
  body: { fontFamily: fonts.sans, fontSize: 13, lineHeight: 1.5, color: colors.textSecondary, marginTop: 8 },
  actions: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16 },
  skip: { border: "none", background: "transparent", fontFamily: fonts.sans, fontSize: 12, fontWeight: 600, color: colors.textTertiary, cursor: "pointer", padding: "6px 2px" },
  back: { padding: "7px 14px", borderRadius: 999, border: `1px solid ${colors.borderStrong}`, background: "#fff", fontFamily: fonts.sans, fontSize: 12.5, fontWeight: 600, color: colors.textSecondary, cursor: "pointer" },
  next: { padding: "7px 18px", borderRadius: 999, border: "none", background: colors.textPrimary, color: "#fff", fontFamily: fonts.sans, fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  nextDisabled: { opacity: 0.5, cursor: "wait" },
  dots: { display: "flex", gap: 6, justifyContent: "center", marginTop: 14 },
  dot: { width: 6, height: 6, borderRadius: 999, background: "rgba(0,0,0,0.14)", transition: "background 0.2s, width 0.2s" },
  dotOn: { width: 18, background: colors.brand },
};
